/**
 * IncidentReview — read-only post-incident review & timeline playback.
 *
 * Visualises an SOS purely from existing data (append-only audit + location
 * history). Play / Pause / Fast-forward / Rewind / scrub animate the driver's
 * breadcrumb over time; the timeline lists every event with timestamps; the
 * context (driver/vehicle/ride/passenger) is shown throughout. Export/print is
 * the server-generated incident PDF. No dispatch/ride state is touched.
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Play, Pause, FastForward, Rewind, X, Printer, ShieldAlert, Clock, Car,
  User, MapPin, FileText,
} from "lucide-react";
import { SmartMap } from "../../components/SmartMap";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const SOS_RED = "#DC2626";
const SOS_AMBER = "#F59E0B";
const TICK_MS = 100;
const FULL_PLAYBACK_S = 20;            // a 1× run spans ~20s regardless of real duration
const STEP = TICK_MS / 1000 / FULL_PLAYBACK_S;

const AUDIT_LABELS = {
  raised: "SOS activated", escalated: "Escalated to Silent Emergency",
  updated: "SOS updated by driver", note: "Dispatcher note added",
  message_driver: "Dispatcher messaged driver (SMS)", resolved: "Incident resolved",
  driver_stand_down: "Driver stood down (marked safe)",
};
const label = (a) => AUDIT_LABELS[a] || (a || "").replace(/_/g, " ");
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB") : "—");
const ms = (iso) => (iso ? Date.parse(iso) : NaN);

export const IncidentReview = ({ sosId, onClose }) => {
  const [d, setD] = useState(null);
  const [frac, setFrac] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [dir, setDir] = useState(1);
  const [speed, setSpeed] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const nonceRef = useRef(0);

  useEffect(() => {
    api.get(`/admin/sos/${sosId}`).then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e)));
  }, [sosId]);

  const emergency = d?.level === "emergency";
  const color = emergency ? SOS_RED : SOS_AMBER;
  const pings = useMemo(() => (d?.location_history || []).filter((p) => p.lat != null), [d]);
  const audit = useMemo(() => (d?.audit || []).slice().sort((a, b) => ms(a.ts) - ms(b.ts)), [d]);

  const [t0, tEnd] = useMemo(() => {
    const times = [
      ...(d?.created_at ? [ms(d.created_at)] : []),
      ...(d?.resolved_at ? [ms(d.resolved_at)] : []),
      ...pings.map((p) => ms(p.ts)),
      ...audit.map((a) => ms(a.ts)),
    ].filter((x) => !Number.isNaN(x));
    if (!times.length) return [0, 0];
    return [Math.min(...times), Math.max(...times)];
  }, [d, pings, audit]);

  const span = Math.max(1, tEnd - t0);
  const currentTs = t0 + frac * span;

  // Playback ticker.
  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      setFrac((f) => {
        const next = f + dir * speed * STEP;
        if (next >= 1) { setPlaying(false); return 1; }
        if (next <= 0) { setPlaying(false); return 0; }
        return next;
      });
    }, TICK_MS);
    return () => clearInterval(id);
  }, [playing, dir, speed]);

  const trail = useMemo(() => pings.filter((p) => ms(p.ts) <= currentTs + 1), [pings, currentTs]);
  const current = trail.length ? trail[trail.length - 1] : (pings[0] || null);
  const activeEvents = audit.filter((a) => ms(a.ts) <= currentTs + 1);
  const currentLabel = activeEvents.length ? label(activeEvents[activeEvents.length - 1].action) : "Before activation";

  // Map overlay (follow the playback position).
  nonceRef.current += 1;
  const markers = [];
  if (pings[0]) markers.push({ id: "start", lat: pings[0].lat, lng: pings[0].lng, type: "pickup", label: "Start", color: "#6B7280" });
  if (current) markers.push({ id: "cur", lat: current.lat, lng: current.lng, type: "sos", kind: "sos", color, pulse: true, label: d?.driver_name || "driver" });
  const routes = trail.length >= 2 ? [{ points: trail.map((p) => ({ lat: p.lat, lng: p.lng })), color, weight: 4, opacity: 0.9 }] : null;
  const focus = current ? { lat: current.lat, lng: current.lng, nonce: nonceRef.current, zoom: pings.length ? 15 : undefined } : null;

  const play = (d2) => { setDir(d2); setPlaying(true); };
  const cycleSpeed = () => setSpeed((s) => (s === 1 ? 2 : s === 2 ? 4 : 1));
  const seekToEvent = useCallback((a) => {
    setPlaying(false);
    setFrac(Math.min(1, Math.max(0, (ms(a.ts) - t0) / span)));
  }, [t0, span]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get(`/admin/sos/${sosId}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");           // view → print or save
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e) || "Could not generate report"); }
    finally { setDownloading(false); }
  };

  if (!d) {
    return (
      <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center" data-testid="incident-review">
        <div className="bg-white rounded-xl px-6 py-4 text-sm text-muted-foreground">Loading incident…</div>
      </div>
    );
  }

  const ride = d.ride || {};
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-3" data-testid="incident-review">
      <div className="bg-white w-full max-w-5xl max-h-[94vh] overflow-auto rounded-2xl p-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-bold flex items-center gap-2"><FileText className="w-5 h-5" /> Incident Review</h3>
            <div className="text-[11px] text-muted-foreground">Read-only playback & audit · operator alerting tool (not 999)</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadPdf} disabled={downloading} data-testid="incident-export-pdf"
              className="text-xs font-semibold bg-[#0A0A0A] text-white rounded-lg px-3 py-2 flex items-center gap-1.5 disabled:opacity-50">
              <Printer className="w-3.5 h-3.5" /> {downloading ? "Generating…" : "Export / Print PDF"}
            </button>
            <button onClick={onClose} data-testid="incident-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Context bar */}
        <div className="grid sm:grid-cols-4 gap-2 mt-3 text-[11px]">
          <div className="border border-border rounded-lg p-2"><div className="label-mini text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" /> Driver</div>{d.driver_name || "—"}<div className="text-muted-foreground">{d.driver_phone || ""}</div></div>
          <div className="border border-border rounded-lg p-2"><div className="label-mini text-muted-foreground flex items-center gap-1"><Car className="w-3 h-3" /> Vehicle</div>{d.vehicle?.make || "—"}<div className="text-muted-foreground">{d.vehicle?.plate || ""}</div></div>
          <div className="border border-border rounded-lg p-2"><div className="label-mini text-muted-foreground flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Level / State</div>{emergency ? "Silent Emergency" : "Assistance"}<div className="text-muted-foreground">{d.state}</div></div>
          <div className="border border-border rounded-lg p-2"><div className="label-mini text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> Trip</div>{ride.passenger_name ? ride.passenger_name : "Not on a trip"}<div className="text-muted-foreground truncate">{ride.pickup_address ? `${ride.pickup_address} →` : ""}</div></div>
        </div>

        {/* Map + timeline */}
        <div className="grid lg:grid-cols-3 gap-3 mt-3">
          <div className="lg:col-span-2 relative rounded-xl overflow-hidden border border-border" style={{ height: 360 }}>
            <SmartMap markers={markers} routes={routes} focusTarget={focus} disableAutoFit showRoute={false} className="absolute inset-0" onMarkerClick={() => {}} />
            <div className="absolute top-2 left-2 z-10 bg-white/90 rounded-lg px-2 py-1 text-[11px] font-semibold shadow" data-testid="incident-clock">
              <Clock className="w-3 h-3 inline mr-1" /> {fmt(new Date(currentTs).toISOString())} · {currentLabel}
            </div>
          </div>

          {/* Event timeline */}
          <div className="border border-border rounded-xl p-2 overflow-auto" style={{ maxHeight: 360 }} data-testid="incident-timeline">
            <div className="label-mini text-muted-foreground mb-1">Timeline</div>
            {audit.map((a) => {
              const reached = ms(a.ts) <= currentTs + 1;
              return (
                <button key={a.id} onClick={() => seekToEvent(a)} data-testid={`incident-event-${a.action}`}
                  className={`w-full text-left text-[11px] rounded-lg px-2 py-1.5 mb-1 border ${reached ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-transparent opacity-50"} hover:bg-secondary`}>
                  <div className="font-semibold flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: reached ? color : "#D1D5DB" }} />
                    {label(a.action)}
                  </div>
                  <div className="text-muted-foreground">{fmt(a.ts)}{a.actor_name ? ` · ${a.actor_name}` : ""}</div>
                  {a.detail && <div className="text-muted-foreground italic">{a.detail}</div>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Playback controls */}
        <div className="mt-3 border border-border rounded-xl p-3">
          <div className="flex items-center gap-2">
            <button onClick={() => play(-1)} data-testid="incident-rewind" className="p-2 rounded-lg border border-border hover:bg-secondary" title="Rewind"><Rewind className="w-4 h-4" /></button>
            <button onClick={() => setPlaying((p) => !p)} data-testid="incident-playpause" className="p-2.5 rounded-lg bg-[#0A0A0A] text-white" title="Play / Pause">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <button onClick={() => play(1)} data-testid="incident-forward" className="p-2 rounded-lg border border-border hover:bg-secondary" title="Play forward"><Play className="w-4 h-4" /></button>
            <button onClick={cycleSpeed} data-testid="incident-speed" className="px-2.5 py-2 rounded-lg border border-border hover:bg-secondary text-xs font-semibold flex items-center gap-1"><FastForward className="w-3.5 h-3.5" /> {speed}×</button>
            <input type="range" min="0" max="1000" value={Math.round(frac * 1000)} data-testid="incident-scrub"
              onChange={(e) => { setPlaying(false); setFrac(Number(e.target.value) / 1000); }}
              className="flex-1 accent-[#DC2626]" />
          </div>
          <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1.5">
            <span>{fmt(d.created_at)}</span>
            <span>Activated → Resolved · {pings.length} location points</span>
            <span>{fmt(d.resolved_at) === "—" ? "ongoing" : fmt(d.resolved_at)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentReview;
